/**
 * QRMaps Design System - Styled Components Utilities
 *
 * Helper functions and styled-components setup
 */

import { css } from 'styled-components';
import { mediaQueries } from '../tokens/breakpoints';

/**
 * Responsive CSS helper - mobile-first approach
 * Usage: ${respondTo.md`padding: 20px;`}
 */
export const respondTo = Object.keys(mediaQueries).reduce((acc, label) => {
  acc[label] = (...args) => css`
    ${mediaQueries[label]} {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

/**
 * Convert hex color to rgba
 * Usage: hexToRgba('#007bff', 0.5) => 'rgba(0, 123, 255, 0.5)'
 */
export const hexToRgba = (hex, alpha = 1) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Get size value with unit
 * Usage: getSize(16) => '16px', getSize('1rem') => '1rem'
 */
export const getSize = (size) => {
  if (typeof size === 'number') return `${size}px`;
  return size;
};

/**
 * Truncate text with ellipsis
 * Usage: ${truncateText(2)} // 2 lines
 */
export const truncateText = (lines = 1) => css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
`;

/**
 * Visually hide element but keep it accessible
 */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

/**
 * Reset button styles
 */
export const resetButton = css`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  outline: none;

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }
`;

/**
 * Focus visible styles (keyboard navigation)
 */
export const focusVisible = (color = '#007bff') => css`
  &:focus-visible {
    outline: 2px solid ${color};
    outline-offset: 2px;
  }
`;

/**
 * Smooth scrolling
 */
export const smoothScroll = css`
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
`;

/**
 * Custom scrollbar styles
 */
export const customScrollbar = (color = '#e0e0e0', hoverColor = '#bdbdbd') => css`
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${color};
    border-radius: 4px;

    &:hover {
      background: ${hoverColor};
    }
  }
`;